import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { isPhone } from '#src/app/shared/utils/userAgent';

import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['petitionDetailsStickySignCtaExperiment'];

type Params = {
	experiment: Experiment;
	utilityContext: UtilityContext;
};

/**
 * Pre-filters the assignment on what the server can know, and deliberately does **not** treat.
 *
 * Treating here would write an `experiment_variations` row for every phone viewer who passes the FC
 * gate, including the ones who never see a sign CTA at all — decision makers, closed/victorious
 * petitions, already-signed viewers, and video-first-hero pages. Those conditions depend on
 * client-side page state, so the consuming hook (CHANGE-98448) is the first place that knows them,
 * and it treats there once `eligible` is computed. Forcing `treated: false` downstream would only
 * suppress `$exposure` — the row would already exist, diluting the measured lift in both arms.
 *
 * `useTreatSignFormAddressRemovalExperiment` on this page is the precedent, and its normalizer
 * (`normalizeSignFormAddressRemovalExperiment`) is sync and non-treating for the same reason.
 *
 * Returns the full experiment object rather than a bare variation string so the hook can pass it
 * straight into `useTrackExperimentExposure`.
 */
export function normalizePetitionDetailsStickySignCtaExperiment({ experiment, utilityContext }: Params): Experiment {
	if (!shouldRunExperiment({ experiment, utilityContext })) {
		// Experiment Service bucketing is keyed on UUID alone, so `experiment.variation`/`treated` can
		// already reflect a treatment arm here even though this user shouldn't see one (non-phone or FC
		// disabled) — e.g. a user treated on a prior phone visit revisiting on desktop. Force `control`
		// so the rendered UI respects the gate, and force `treated: false` so `useTrackExperimentExposure`
		// doesn't fire a control `$exposure` for this out-of-scope visit.
		return { ...experiment, variation: 'control', treated: false };
	}

	return experiment;
}

/**
 * Deliberately `isPhone()` rather than the `isMobile()` the sibling normalizers use. The sticky bar
 * this experiment repositions lives in the mobile action-button tree, which is CSS-hidden at the
 * `md` breakpoint (`block md:hidden`) rather than gated by device type, and `isMobile()` also matches
 * tablets.
 *
 * Device type is an approximation of that breakpoint, not a guarantee: a phone in landscape can
 * exceed 768px and be bucketed while the bar is hidden, and a small tablet under 768px is excluded
 * despite rendering it. It is a much closer proxy than `isMobile()` — which admits every tablet —
 * but the residual mismatch is real, and the exact viewport isn't knowable server-side, where the
 * arm has to be resolved because the treatment is the first paint.
 *
 * The remaining eligibility conditions the platform can't know — unsigned viewer, non-video-first
 * hero, no cookie wall — depend on client-side page state and are applied in the consuming hook
 * (CHANGE-98448).
 */
function shouldRunExperiment({ experiment, utilityContext }: Params): boolean {
	return isEnabledByFC(experiment) && isPhone(utilityContext);
}

/** An `enabledIf` failure (FC disabled, fetch error, not registered yet, …) surfaces as `fallback: true`. */
function isEnabledByFC(experiment: Experiment): boolean {
	return !experiment.fallback;
}
